import { useQuery } from '@tanstack/react-query';
import { apiCall } from '@/lib/api-client';

import type { UploaderProgressSummary, TierName } from './tierConstants';
import {
  UPLOADER_PROFILE_SUMMARY_KEY,
  UPLOADER_HISTORY_KEY,
  ADMIN_UPLOADER_PROFILE_KEY,
  ADMIN_UPLOADERS_PROGRESS_OVERVIEW_KEY,
} from './progressKeys';

export type UploaderHistoryRow = {
  periodYear: number;
  periodMonth: number;
  totalUploads: number;
  primaryUploads: number;
  subUploads: number;
  noMedicaidUploads: number;
  tierReached: TierName;
  workingDaysInPeriod: number;
  daysAtOrAboveTarget: number;
  longestStreak: number;
  isCurrentMonth: boolean;
};

export type AdminUploaderRow = {
  uploaderId: string;
  displayName: string;
  email: string;
  active: boolean;
  uploadsToday: number;
  uploadsMonth: number;
  primaryUploadsMonth: number;
  subUploadsMonth: number;
  noMedicaidUploadsMonth: number;
  tierThisMonth: TierName;
  tierMaintained: TierName;
  tierDisplayed: TierName;
  longestStreakThisMonth: number;
};

export type AdminUploadersOverviewResponse = {
  items: AdminUploaderRow[];
  tierDistribution: {
    below_floor: number;
    base: number;
    t1: number;
    t2: number;
    t3: number;
  };
};

export function useUploaderProfileSummary() {
  return useQuery<UploaderProgressSummary>({
    queryKey: UPLOADER_PROFILE_SUMMARY_KEY,
    queryFn: () => apiCall<UploaderProgressSummary>('/uploader/me/profile-summary'),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useUploaderHistory(months: number = 12) {
  return useQuery<UploaderHistoryRow[]>({
    queryKey: UPLOADER_HISTORY_KEY(months),
    queryFn: () => apiCall<UploaderHistoryRow[]>(`/uploader/me/history?months=${months}`),
    staleTime: 300_000,
  });
}

export function useAdminUploaderProgress(
  uploaderId: number | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<UploaderProgressSummary>({
    queryKey: ADMIN_UPLOADER_PROFILE_KEY(uploaderId ?? -1),
    queryFn: () => apiCall<UploaderProgressSummary>(`/admin/uploaders/${uploaderId}/profile-summary`),
    enabled: opts.enabled !== false && uploaderId != null && uploaderId > 0,
    staleTime: 60_000,
  });
}

export function useAdminUploadersProgressOverview() {
  return useQuery<AdminUploadersOverviewResponse>({
    queryKey: ADMIN_UPLOADERS_PROGRESS_OVERVIEW_KEY,
    queryFn: () => apiCall<AdminUploadersOverviewResponse>('/admin/uploaders-progress-overview'),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}
